import React, { useEffect, useState } from "react";
import { CandlestickChart, Power, ShieldAlert, Loader2, Circle, ArrowUpFromLine, Check, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { cn } from "@/lib/utils";

const fmt = (n, d = 2) => (n === null || n === undefined ? "—" : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));

function Toggle({ label, value, onChange, danger, disabled, tid }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3">
      <span className={cn("text-sm font-semibold", danger ? "text-rose-300" : "text-white")}>{label}</span>
      <button onClick={() => !disabled && onChange(!value)} disabled={disabled} data-testid={tid}
        className={cn("relative h-6 w-11 rounded-full transition-colors disabled:opacity-40",
          value ? (danger ? "bg-rose-500" : "bg-emerald-500") : "bg-white/20")}>
        <span className={cn("absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all", value ? "left-[22px]" : "left-0.5")} />
      </button>
    </div>
  );
}

export default function AdminTrading() {
  const [s, setS] = useState(null);
  const [ov, setOv] = useState(null);
  const [positions, setPositions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [addr, setAddr] = useState("");
  const [feePct, setFeePct] = useState("");
  const [maxPend, setMaxPend] = useState("");
  const [wds, setWds] = useState([]);
  const [wdCounts, setWdCounts] = useState({});
  const [wdFilter, setWdFilter] = useState("requested");
  const [sel, setSel] = useState([]);

  const loadWds = (status = wdFilter) => {
    api.get(`/trading/admin/withdrawals${status ? `?status=${status}` : ""}`).then((r) => { setWds(r.data.withdrawals || []); setWdCounts(r.data.counts || {}); setSel([]); }).catch(() => {});
  };

  const load = () => {
    api.get("/trading/admin/settings").then((r) => { setS(r.data); setAddr((a) => a || r.data.deposit_address || ""); setFeePct((v) => v !== "" ? v : String(r.data.withdraw_fee_pct ?? 0)); setMaxPend((v) => v !== "" ? v : String(r.data.max_pending_withdrawals ?? 5)); }).catch(() => {});
    api.get("/trading/admin/overview").then((r) => setOv(r.data)).catch(() => {});
    api.get("/trading/admin/positions").then((r) => setPositions(r.data.open_positions || [])).catch(() => {});
    api.get("/trading/admin/accounts").then((r) => setAccounts(r.data.accounts || [])).catch(() => {});
    loadWds();
  };
  useEffect(() => { load(); const id = setInterval(load, 8000); return () => clearInterval(id); }, []); // eslint-disable-line
  useEffect(() => { loadWds(wdFilter); }, [wdFilter]); // eslint-disable-line

  const approveWd = async (wd) => {
    if (!window.confirm(`Approve ${wd.source} withdrawal of $${fmt(wd.net)} to ${wd.wallet_address}? Payout will be sent (mock in preview / on-chain in production).`)) return;
    try { const r = await api.post(`/trading/admin/withdrawals/${wd.wd_id}/approve`); toast.success(r.data.withdrawal?.status === "paid" ? "Approved & paid" : `Payout ${r.data.withdrawal?.status}`); loadWds(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };
  const rejectWd = async (wd) => {
    const reason = window.prompt("Reason for rejection (funds refunded to the member's source wallet):", "Rejected by admin");
    if (reason === null) return;
    try { await api.post(`/trading/admin/withdrawals/${wd.wd_id}/reject`, { reason }); toast.success("Rejected & refunded"); loadWds(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
  };
  const toggleSel = (id) => setSel((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  const reqIds = wds.filter((w) => w.status === "requested").map((w) => w.wd_id);
  const allSelected = reqIds.length > 0 && reqIds.every((id) => sel.includes(id));
  const toggleAll = () => setSel(allSelected ? [] : reqIds);
  const bulkApprove = async () => {
    if (sel.length === 0) return;
    if (!window.confirm(`Approve ${sel.length} selected withdrawal(s)? Payouts will be sent (mock in preview / on-chain in production).`)) return;
    try { const r = await api.post("/trading/admin/withdrawals/bulk-approve", { wd_ids: sel }); toast.success(`${r.data.approved.length} approved${r.data.skipped.length ? `, ${r.data.skipped.length} skipped` : ""}`); loadWds(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Bulk approve failed"); }
  };

  const save = async (patch) => {
    setSaving(true);
    try { const r = await api.post("/trading/admin/settings", patch); setS({ ...s, ...r.data }); toast.success("Trading settings updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); } finally { setSaving(false); }
  };
  const emergency = async () => {
    if (!window.confirm("EMERGENCY STOP will disable engine, self-trade, bot, autopilot and live execution immediately. Continue?")) return;
    try { await api.post("/trading/admin/emergency-stop"); toast.success("Emergency stop engaged — all trading disabled"); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const toggleBlock = async (uid, blocked) => { try { await api.post(`/trading/admin/user/${uid}/block`, { blocked }); toast.success(blocked ? "Member trading disabled" : "Member trading enabled"); load(); } catch { toast.error("Failed"); } };
  const closeAll = async (uid) => { if (!window.confirm("Close ALL open positions for this member at market?")) return; try { const r = await api.post(`/trading/admin/user/${uid}/close-all`); toast.success(`Closed ${r.data.closed} position(s)`); load(); } catch { toast.error("Failed"); } };
  const adjust = async (uid) => { const v = window.prompt("Adjust member Trading Wallet by (negative to deduct):", "0"); if (v === null || v === "") return; try { const r = await api.post(`/trading/admin/user/${uid}/adjust`, { amount: Number(v), note: "admin adjust" }); toast.success(`New balance $${r.data.balance}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };

  if (!s) return <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-[var(--gold)]" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-trading">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-xl font-bold text-white"><CandlestickChart className="text-[var(--gold)]" /> Trading Control Center</h1>
        <button onClick={emergency} data-testid="emergency-stop-btn"
          className="flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-rose-600/30 hover:bg-rose-700">
          <ShieldAlert size={16} /> EMERGENCY STOP
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <p className="mb-3 flex items-center gap-2 text-sm font-bold text-white"><Power size={15} className="text-emerald-400" /> Engine Modes</p>
          <div className="space-y-2">
            <Toggle label="Trading Engine" value={s.engine} onChange={(v) => save({ engine: v })} disabled={saving} tid="tgl-engine" />
            <Toggle label="Self Trading" value={s.self_trade} onChange={(v) => save({ self_trade: v })} disabled={saving} tid="tgl-self" />
            <Toggle label="Bot Trading" value={s.bot_trade} onChange={(v) => save({ bot_trade: v })} disabled={saving} tid="tgl-bot" />
            <Toggle label="Bot Auto-Pilot (scheduled)" value={s.autopilot} onChange={(v) => save({ autopilot: v })} disabled={saving} tid="tgl-autopilot" />
            <Toggle label="Auto-Approve Self-Trade Withdrawals" value={s.auto_approve} onChange={(v) => save({ auto_approve: v })} disabled={saving} tid="tgl-auto-approve" />
            <Toggle label="Live Execution (real money)" value={s.live_execution} onChange={(v) => save({ live_execution: v })} danger disabled={saving || !s.env_live_allowed} tid="tgl-live" />
          </div>
          <p className="mt-2 text-[10px] text-[var(--text-dim)]">Auto-Pilot runs every 30 min via cron and only trades when Engine + Bot Trading are ON.</p>
          <div className="mt-3 rounded-lg border border-white/10 bg-black/20 p-2">
            <p className="mb-1 text-[11px] font-bold text-white">On-chain Trading Deposit Address (USDT · BEP20)</p>
            <div className="flex gap-2">
              <input value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="0x… USDT BEP20 receiving address" data-testid="trading-deposit-address"
                className="w-full rounded border border-white/10 bg-black/30 px-2 py-1 text-[11px] text-white outline-none" />
              <button onClick={() => save({ deposit_address: addr.trim() })} data-testid="save-deposit-address" className="rounded bg-emerald-500 px-3 py-1 text-[11px] font-bold text-black hover:bg-emerald-400">Save</button>
            </div>
            <p className="mt-1 text-[10px] text-[var(--text-dim)]">Members send USDT (BEP20) here to fund Trading Capital; deposits auto-verify on-chain. Keep this SEPARATE from your MLM deposit address so self-trade funds never mix.</p>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-white/10 bg-black/20 p-2">
              <p className="mb-1 text-[11px] font-bold text-white">Withdrawal Fee (%)</p>
              <div className="flex gap-2">
                <input type="number" min="0" max="50" step="0.1" value={feePct} onChange={(e) => setFeePct(e.target.value)} data-testid="withdraw-fee-pct"
                  className="w-full rounded border border-white/10 bg-black/30 px-2 py-1 text-[11px] text-white outline-none" />
                <button onClick={() => save({ withdraw_fee_pct: Number(feePct) })} data-testid="save-withdraw-fee" className="rounded bg-emerald-500 px-3 py-1 text-[11px] font-bold text-black hover:bg-emerald-400">Save</button>
              </div>
              <p className="mt-1 text-[10px] text-[var(--text-dim)]">Deducted from each self-trade payout. 0 = no fee.</p>
            </div>
            <div className="rounded-lg border border-white/10 bg-black/20 p-2">
              <p className="mb-1 text-[11px] font-bold text-white">Max Pending Withdrawals / Member</p>
              <div className="flex gap-2">
                <input type="number" min="0" step="1" value={maxPend} onChange={(e) => setMaxPend(e.target.value)} data-testid="max-pending-wd"
                  className="w-full rounded border border-white/10 bg-black/30 px-2 py-1 text-[11px] text-white outline-none" />
                <button onClick={() => save({ max_pending_withdrawals: Number(maxPend) })} data-testid="save-max-pending" className="rounded bg-emerald-500 px-3 py-1 text-[11px] font-bold text-black hover:bg-emerald-400">Save</button>
              </div>
              <p className="mt-1 text-[10px] text-[var(--text-dim)]">0 = unlimited open requests.</p>
            </div>
          </div>
          <div className="mt-3 rounded-lg border border-amber-400/30 bg-amber-400/10 p-2 text-[11px] text-amber-300">
            {s.live_adapters_wired
              ? "Live adapters are wired."
              : "Live execution adapters are NOT wired yet (Phase 4). All fills remain PAPER regardless of this toggle. Provide exchange READ+TRADE keys + MT5 bridge to enable real trading."}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-[var(--text-dim)]">Mode:</span>
            {["PAPER", "TESTNET", "LIVE"].map((m) => (
              <button key={m} onClick={() => save({ mode: m })} disabled={saving || (m === "LIVE" && !s.env_live_allowed)} data-testid={`mode-${m}`}
                className={cn("rounded-lg px-3 py-1 text-xs font-bold", s.mode === m ? "bg-[var(--gold)] text-black" : "bg-white/5 text-[var(--text-dim)]", m === "LIVE" && !s.env_live_allowed && "opacity-40")}>{m}</button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <p className="mb-3 text-sm font-bold text-white">Overview</p>
          {ov && (
            <div className="grid grid-cols-2 gap-2">
              {[["Open Positions", ov.open_positions], ["Total Trades", ov.total_trades], ["Accounts", ov.accounts], ["Mode", s.mode]].map(([l, v]) => (
                <div key={l} className="rounded-xl bg-black/30 px-3 py-3"><p className="text-[10px] uppercase text-[var(--text-dim)]">{l}</p><p className="text-lg font-bold text-white">{v}</p></div>
              ))}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
            {[["engine", s.engine], ["self", s.self_trade], ["bot", s.bot_trade], ["live", s.live_execution]].map(([l, on]) => (
              <span key={l} className={cn("flex items-center gap-1 rounded-full px-2 py-1 font-semibold", on ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-[var(--text-dim)]")}>
                <Circle size={7} className="fill-current" /> {l} {on ? "ON" : "OFF"}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
        <p className="mb-2 text-sm font-bold text-white">Open Positions (all members) — {positions.length}</p>
        {positions.length === 0 ? <p className="py-4 text-center text-sm text-[var(--text-dim)]">No open positions</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-[var(--text-dim)]"><tr><th className="py-1">Engine</th><th>Symbol</th><th>Side</th><th>Amount</th><th>Entry</th><th>Mode</th><th>Opened</th></tr></thead>
              <tbody>
                {positions.map((p) => (
                  <tr key={p.id} className="border-t border-white/5">
                    <td className="py-1.5"><span className={cn("rounded px-1.5 py-0.5 text-[10px] font-bold", p.engine === "BOT" ? "bg-emerald-500/20 text-emerald-400" : "bg-sky-500/20 text-sky-400")}>{p.engine || "SELF"}</span></td>
                    <td className="font-semibold text-white">{p.symbol}</td>
                    <td className={p.side === "BUY" ? "text-emerald-400" : "text-rose-400"}>{p.side}</td>
                    <td className="text-white">${fmt(p.amount)}</td>
                    <td className="text-white">{fmt(p.entry, 2)}</td>
                    <td className="text-amber-300">{p.mode}</td>
                    <td className="text-[var(--text-dim)]">{p.opened_at?.slice(5, 16).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="admin-trading-accounts">
        <p className="mb-2 text-sm font-bold text-white">Member Trading Accounts — {accounts.length}</p>
        {accounts.length === 0 ? <p className="py-4 text-center text-sm text-[var(--text-dim)]">No trading accounts yet</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-[var(--text-dim)]"><tr><th className="py-1">Member</th><th>Wallet</th><th>Locked</th><th>Profit</th><th>Bot Alloc</th><th>Open</th><th>Status</th><th className="text-right">Controls</th></tr></thead>
              <tbody>
                {accounts.map((a, idx) => (
                  <tr key={a.user_id + '-' + idx} className="border-t border-white/5" data-testid={`acct-row-${a.member_id}`}>
                    <td className="py-1.5"><span className="font-semibold text-white">{a.member_id}</span><span className="ml-1 text-[var(--text-dim)]">{a.name}</span></td>
                    <td className="text-white">${fmt(a.balance)}</td>
                    <td className="text-white">${fmt(a.locked)}</td>
                    <td className={a.realized_pnl >= 0 ? "text-emerald-400" : "text-rose-400"}>${fmt(a.realized_pnl)}</td>
                    <td className="text-white">${fmt(a.bot_allocated)}</td>
                    <td className="text-white">{a.open_positions}</td>
                    <td>{a.blocked ? <span className="rounded bg-rose-500/20 px-1.5 py-0.5 text-[10px] font-bold text-rose-400">BLOCKED</span> : <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold text-emerald-400">ACTIVE</span>}</td>
                    <td className="py-1.5">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => toggleBlock(a.user_id, !a.blocked)} data-testid={`acct-block-${a.member_id}`}
                          className={cn("rounded px-2 py-0.5 text-[10px] font-bold", a.blocked ? "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30" : "bg-rose-500/20 text-rose-400 hover:bg-rose-500/30")}>{a.blocked ? "Enable" : "Disable"}</button>
                        <button onClick={() => adjust(a.user_id)} data-testid={`acct-adjust-${a.member_id}`} className="rounded bg-amber-400/20 px-2 py-0.5 text-[10px] font-bold text-amber-300 hover:bg-amber-400/30">Adjust</button>
                        <button onClick={() => closeAll(a.user_id)} disabled={!a.open_positions} data-testid={`acct-closeall-${a.member_id}`} className="rounded bg-sky-500/20 px-2 py-0.5 text-[10px] font-bold text-sky-300 hover:bg-sky-500/30 disabled:opacity-30">Close All</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="admin-trade-withdrawals">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-2 text-sm font-bold text-white"><ArrowUpFromLine size={15} className="text-emerald-400" /> Self-Trade Withdrawals</p>
          <div className="flex flex-wrap gap-1 text-[11px]">
            {["requested", "paid", "rejected", "failed"].map((st) => (
              <button key={st} onClick={() => setWdFilter(st)} data-testid={`wd-filter-${st}`}
                className={cn("rounded-lg px-2.5 py-1 font-bold capitalize", wdFilter === st ? "bg-[var(--gold)] text-black" : "bg-white/5 text-[var(--text-dim)]")}>
                {st} {wdCounts[st] ? `(${wdCounts[st]})` : ""}
              </button>
            ))}
          </div>
        </div>
        {wdFilter === "requested" && reqIds.length > 0 && (
          <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg bg-black/20 px-3 py-2" data-testid="wd-bulk-bar">
            <label className="flex items-center gap-1.5 text-[11px] font-semibold text-white">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="wd-select-all" className="h-3.5 w-3.5 accent-emerald-500" />
              Select all ({reqIds.length})
            </label>
            <span className="text-[11px] text-[var(--text-dim)]">{sel.length} selected</span>
            <button onClick={bulkApprove} disabled={sel.length === 0} data-testid="wd-bulk-approve"
              className="ml-auto flex items-center gap-1 rounded-lg bg-emerald-500 px-3 py-1.5 text-[11px] font-bold text-black hover:bg-emerald-400 disabled:opacity-40">
              <Check size={13} /> Approve Selected ({sel.length})
            </button>
          </div>
        )}
        {wds.length === 0 ? <p className="py-4 text-center text-sm text-[var(--text-dim)]">No {wdFilter} withdrawals</p> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-xs">
              <thead className="text-[var(--text-dim)]"><tr>{wdFilter === "requested" && <th className="py-1 w-6"></th>}<th className="py-1">Member</th><th>Source</th><th>Gross</th><th>Net</th><th>BEP20 Address</th><th>Tx</th><th>Status</th><th className="text-right">Action</th></tr></thead>
              <tbody>
                {wds.map((w) => (
                  <tr key={w.wd_id} className="border-t border-white/5" data-testid={`wd-admin-row-${w.wd_id}`}>
                    {wdFilter === "requested" && <td className="py-1.5">{w.status === "requested" && <input type="checkbox" checked={sel.includes(w.wd_id)} onChange={() => toggleSel(w.wd_id)} data-testid={`wd-select-${w.wd_id}`} className="h-3.5 w-3.5 accent-emerald-500" />}</td>}
                    <td className="py-1.5"><span className="font-semibold text-white">{w.member_id}</span><span className="ml-1 text-[var(--text-dim)]">{w.name}</span></td>
                    <td><span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white/70">{w.source}</span></td>
                    <td className="text-white">${fmt(w.gross)}</td>
                    <td className="font-bold text-emerald-300">${fmt(w.payout ?? w.net)}</td>
                    <td className="max-w-[160px] truncate font-mono text-[10px] text-white/70" title={w.wallet_address}>{w.wallet_address}</td>
                    <td>{w.tx_hash ? <a href={`https://bscscan.com/tx/${w.tx_hash}`} target="_blank" rel="noreferrer" className="font-mono text-emerald-400 underline">{w.tx_hash.slice(0, 8)}…</a> : "—"}</td>
                    <td><span className={cn("rounded px-1.5 py-0.5 text-[10px] font-bold uppercase",
                      w.status === "paid" ? "bg-emerald-500/20 text-emerald-300" : w.status === "requested" ? "bg-amber-500/20 text-amber-300" : "bg-rose-500/20 text-rose-300")}>{w.status}</span></td>
                    <td className="py-1.5">
                      {w.status === "requested" ? (
                        <div className="flex justify-end gap-1">
                          <button onClick={() => approveWd(w)} data-testid={`wd-approve-${w.wd_id}`} className="flex items-center gap-0.5 rounded bg-emerald-500/20 px-2 py-0.5 text-[10px] font-bold text-emerald-400 hover:bg-emerald-500/30"><Check size={11} /> Approve</button>
                          <button onClick={() => rejectWd(w)} data-testid={`wd-reject-${w.wd_id}`} className="flex items-center gap-0.5 rounded bg-rose-500/20 px-2 py-0.5 text-[10px] font-bold text-rose-400 hover:bg-rose-500/30"><XCircle size={11} /> Reject</button>
                        </div>
                      ) : <span className="flex justify-end text-[10px] text-[var(--text-dim)]">{w.note ? w.note.slice(0, 28) : "—"}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
